import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { QUERY_CONFIG } from '@/config/constants';
import { QUERY_KEYS } from '@/config/queries';
import { supabaseQueryWithRetry } from '@/lib/networkRetry';
import { createLogger } from '@/lib/structuredLogger';
import { WEARABLE_CONFIG } from '@/config/wearable';

export interface SyncHistoryEntry {
  id: string;
  user_id: string;
  sync_scope: string | null;
  sync_state: string;
  sync_started_at: string | null;
  sync_ended_at: string | null;
  sync_progress: number | null;
  last_error: string | null;
  created_at: string;
  collapsed_count?: number;
}

interface UseSyncHistoryOptions {
  userId?: string;
  enabled?: boolean;
  limit?: number;
}

const logger = createLogger('useSyncHistory');

const HISTORY_DEDUPE_WINDOW_MS = 24 * 60 * 60 * 1000; // 24 hours

function collapseRepeatedEntries(
  entries: SyncHistoryEntry[],
  maxItems: number,
): SyncHistoryEntry[] {
  const collapsed: SyncHistoryEntry[] = [];

  for (const entry of entries) {
    if (collapsed.length === 0) {
      collapsed.push({ ...entry, collapsed_count: 1 });
      continue;
    }

    const previous = collapsed[collapsed.length - 1];
    const previousTs = new Date(previous.sync_started_at ?? previous.created_at).getTime();
    const currentTs = new Date(entry.sync_started_at ?? entry.created_at).getTime();
    const timeDelta = Math.abs(previousTs - currentTs);

    const sameOutcome =
      previous.sync_scope === entry.sync_scope &&
      previous.sync_state === entry.sync_state &&
      (previous.last_error ?? '') === (entry.last_error ?? '');

    if (sameOutcome && Number.isFinite(timeDelta) && timeDelta <= HISTORY_DEDUPE_WINDOW_MS) {
      previous.collapsed_count = (previous.collapsed_count ?? 1) + 1;
      continue;
    }

    collapsed.push({ ...entry, collapsed_count: 1 });
    if (collapsed.length >= maxItems) break;
  }

  return collapsed;
}

export function useSyncHistory({
  userId,
  enabled = true,
  limit = WEARABLE_CONFIG.SYNC_HISTORY_LIMIT,
}: UseSyncHistoryOptions) {
  return useQuery<SyncHistoryEntry[]>({
    queryKey: QUERY_KEYS.FITBIT_SYNC_HISTORY(userId ?? ''),
    queryFn: async () => {
      if (!userId) return [];

      // Fetch more entries than needed so we can filter client-side
      const { data, error } = await supabaseQueryWithRetry(() =>
        supabase
          .from('fitbit_sync_status_history')
          .select(
            'id, user_id, sync_scope, sync_state, sync_started_at, sync_ended_at, sync_progress, last_error, created_at',
          )
          .eq('user_id', userId)
          .in('sync_state', ['completed', 'failed'])
          .order('sync_started_at', { ascending: false })
          .limit(limit * 4),
      );

      if (error) {
        logger.error('Error fetching sync history', error);
        throw error;
      }

      const typedData = (data ?? []) as SyncHistoryEntry[];
      return collapseRepeatedEntries(typedData, limit);
    },
    enabled: !!userId && enabled,
    staleTime: QUERY_CONFIG.STALE_TIME.MEDIUM,
    gcTime: QUERY_CONFIG.GC_TIME.MEDIUM,
  });
}
